import type { CreateNotePayload, Note, UpdateNotePayload } from "@/schema"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import { useApiContext } from "@/hooks/api/use-api-context"

import {
  bulkAddNoteTag,
  bulkCreateNotes,
  BulkCreateNotesPayload,
  bulkDeleteNotes,
  createNote,
  deleteNote,
  restoreNoteVersion,
  updateNote,
} from "./api"

export function useCreateNote() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      newFiles,
      overrideBookId,
      ...note
    }: CreateNotePayload & { newFiles?: File[]; overrideBookId?: string }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return createNote(context, note, newFiles, overrideBookId)
    },
    meta: { errorToast: { fallback: "Failed to create note" } },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notes"] })
    },
  })
}

export function useRestoreNoteVersion(noteId: string) {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (versionId: string) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return restoreNoteVersion(context, noteId, versionId)
    },
    meta: { errorToast: { fallback: "Failed to restore note version" } },
    onSuccess: (data) => {
      queryClient.setQueryData(["note", noteId], data)
      queryClient.invalidateQueries({ queryKey: ["notes"] })
      queryClient.invalidateQueries({ queryKey: ["note-versions", noteId] })
      queryClient.invalidateQueries({ queryKey: ["note-version", noteId] })
    },
  })
}

export function useUpdateNote() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ newFiles, ...note }: UpdateNotePayload & { newFiles?: File[] }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return updateNote(context, note, newFiles)
    },
    onSuccess: async (data: Note) => {
      queryClient.invalidateQueries({ queryKey: ["notes"] })
      queryClient.invalidateQueries({ queryKey: ["note", data.id] })
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["note-versions", data.id], refetchType: "all" }),
        queryClient.invalidateQueries({ queryKey: ["note-version", data.id], refetchType: "all" }),
      ])
    },
  })
}

export function useDeleteNote() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      await deleteNote(context, id)
    },
    meta: { errorToast: { fallback: "Failed to delete note" } },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notes"] })
    },
  })
}

export function useBulkDeleteNotes() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (ids: string[]) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return bulkDeleteNotes(context, ids)
    },
    meta: { errorToast: { fallback: "Failed to delete notes" } },
    onSettled: () => {
      return queryClient.invalidateQueries({ queryKey: ["notes"] })
    },
  })
}

export function useBulkAddNoteTag() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ notes, tagId }: { notes: Note[]; tagId: string }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return bulkAddNoteTag(context, notes, tagId)
    },
    meta: { errorToast: { fallback: "Failed to add label to notes" } },
    onSettled: () => {
      return queryClient.invalidateQueries({ queryKey: ["notes"] })
    },
  })
}

export function useBulkCreateNotes() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: BulkCreateNotesPayload) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return bulkCreateNotes(context, params)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notes"] })
      queryClient.invalidateQueries({ queryKey: ["lists"] })
      toast.success("Note import jobs queued successfully")
    },
  })
}
